import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import BackIcon from "../../images/BackIcon";
import { webLoadingFalse, webLoadingTrue, webNavigate } from "../../services/navigate.service";
import { getLocations } from "../../services/inventoryCorrections.service";
import { createOrderTemplates, getDetailOrderTemplates, updateOrderTemplates } from "../../services/ordertemplates.service";
import { showToast } from "../../services/toast.service";
import { showApiError } from "../../services/apiError";
import { inventoryFormLimits, limitProps, useFieldLimits, validateAgainstLimits } from "../../services/fieldLimits";
import FieldCounter from "../../components/FieldCounter";
import { useResponsive } from "../../hook/useResponsive";
import { useFormKeyboard } from "../../hook/useFormKeyboard";
import { BORDER, BORDER_SOFT, DESTRUCTIVE, Icon, MONO, TEXT, TEXT_DIM, TEXT_FAINT, TEXT_MUTED } from "../../components/list";
import {
  Change,
  HeaderField,
  LineErrors,
  MSG,
  Problem,
  QtyField,
  TemplateHeader,
  TemplateLine,
  TemplateState,
  addProducts,
  buildPayload,
  dayOf,
  dayToIso,
  detailsToLines,
  duplicateLines,
  filterLines,
  firstProblem,
  headerErrors,
  invalidLineCount,
  listChanges,
  missingOrderQty,
  newHeader,
  pickDay,
  templateToHeader,
  unitsOnTemplate,
  visibleLineErrors,
} from "./orderTemplateLogic";
import {
  ERROR_BG,
  FONT,
  HEAD_BG,
  INK,
  LeaveDialog,
  OT_CSS,
  PillButton,
  VISUALLY_HIDDEN,
  errorTextStyle,
  fieldStyle,
  fill,
  labelStyle,
  tr,
} from "./orderTemplateUi";
import AddProductsDialog, { ProductPick } from "./AddProductsDialog";

// Shapes other pages import from here (Count, Order, Delivery, Supplier screens).
export interface Product {
    id: string;
    sku: string;
    type: number;
    productName: string;
    productGroup: { groupName: string };
    UOM: { uomName: string };
    productPrices: [{ cost: string }];
    inventoryValuation: { unitCost: string };
    updatedAt: Date;
    isActive: boolean;
    isAttributes: true | false;
    currentStock: number;
    productUOMs?: [{ id: string, isBase: boolean, uom: { uomName: string } }]
    productUOMId: string;
}

export interface ProductOrderTemplates {
    id: string;
    productId: string;
    product: {
        sku: string;
        type: number;
        productName: string;
        productGroup: { groupName: string };
        UOM: { uomName: string };
        productPrices: [{ cost: string }];
        inventoryValuation: { unitCost: string };
        updatedAt: Date;
        isActive: boolean;
        isAttributes: true | false;
        currentStock: number;
    }
}

export interface ProductResponse {
    data: {
        productWithCompany: Product[];
        count: number
    }
}

export interface ProductOrderTemplateResponse {
    data: {
        data: {
            orderTemplateDetails: ProductOrderTemplates[];
            count: number
        }
    }
}

export interface ProductAttributesResponse {
    productId: string;
    lowQty: number;
    medQty: number;
    highQty: number;
    productUOMId: string;
}

interface SiteOption {
  id: string;
  name: string;
}

type LeaveRequest = { kind: "back" } | { kind: "host"; proceed: (() => void) | null };

const QTY_FIELDS: QtyField[] = ["lowQty", "medQty", "highQty"];
const COLUMN_ID: Record<QtyField, string> = { lowQty: "ot-col-low", medQty: "ot-col-med", highQty: "ot-col-high" };
const COLUMN_LABEL: Record<QtyField, string> = { lowQty: "Reorder at ≤", medQty: "Order qty", highQty: "Max per order" };
const PHONE_LABEL: Record<QtyField, string> = { lowQty: "Reorder at", medQty: "Order qty", highQty: "Max" };
const HEADER_FIELD_ID: Record<"templateName" | "locationId" | "validTo", string> = {
  templateName: "ot-name",
  locationId: "ot-site",
  validTo: "ot-valid-to",
};
const FIELD_LABEL: Record<HeaderField, string> = {
  templateName: "Template name",
  locationId: "Site",
  description: "Description",
  validFrom: "Valid from",
  validTo: "Valid to",
};
// Room under the phone list for the fixed save bar.
const PHONE_BAR_SPACE = 120;
const TABBAR_OFFSET = "calc(var(--finex-tabbar-h, 0px) + env(safe-area-inset-bottom, 0px))";

const ellipsis: React.CSSProperties = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

const OrderTemplatesDetail: React.FC = () => {
  const { isTouchLayout: phone } = useResponsive();
  const preLanguage = localStorage.getItem("language");
  const listPath = "/" + preLanguage + "/orderTemplates";
  const routeId = window.location.pathname.split("/").pop() || "";
  // After the first save of a new template, until the route reports the new id.
  const [createdId, setCreatedId] = useState<string | null>(null);
  const id = routeId === "new" && createdId ? createdId : routeId;
  const isNew = id === "new";
  const kb = useFormKeyboard({ autoFocus: isNew && !phone });
  const [language, setLanguage] = useState<any>(null);
  const t = (key: string) => tr(language, key);

  const [saved, setSaved] = useState<TemplateState>(() => ({ header: newHeader(), lines: [] }));
  const [header, setHeader] = useState<TemplateHeader>(saved.header);
  const [lines, setLines] = useState<TemplateLine[]>([]);
  const [sites, setSites] = useState<SiteOption[]>([]);
  const [templateSite, setTemplateSite] = useState<SiteOption | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  // Set by a Save refused for problems: empty Order qty boxes turn red and the save bar counts bad lines.
  const [attempted, setAttempted] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [leave, setLeave] = useState<LeaveRequest | null>(null);
  const limits = useFieldLimits(inventoryFormLimits("order-template"));
  const tableRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLInputElement>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  /* ---------------- Loading ---------------- */

  const reset = (state: TemplateState) => {
    setSaved(state);
    setHeader(state.header);
    setLines(state.lines);
    setServerErrors({});
    setTouched({});
    setAttempted(false);
  };

  const loadTemplate = useCallback(async (templateId: string) => {
    webLoadingTrue();
    try {
      const response: any = await getDetailOrderTemplates(templateId);
      const data = response?.data?.data ?? {};
      reset({ header: templateToHeader(data), lines: detailsToLines(data.details) });
      setTemplateSite(data.location?.id ? { id: String(data.location.id), name: String(data.location.name ?? "") } : null);
      loadedRef.current = true;
    } catch (error) {
      showApiError(error, { language: window.languageContext?.language });
    } finally {
      webLoadingFalse();
    }
  }, []);

  const loadSites = useCallback(async () => {
    try {
      const response: any = await getLocations(localStorage.getItem("userId"), localStorage.getItem("companyId"), 0, 0, "");
      const raw = response?.data?.data;
      const list: any[] = Array.isArray(raw) ? raw : Array.isArray(raw?.locations) ? raw.locations : [];
      setSites(list.filter((site) => site?.id).map((site) => ({ id: String(site.id), name: String(site.name ?? "") })));
    } catch (error) {
      showApiError(error, { language: window.languageContext?.language });
    }
  }, []);

  useEffect(() => {
    loadedRef.current = false;
    if (id === "new") {
      reset({ header: newHeader("", dayToIso(dayOf(new Date().toISOString()))), lines: [] });
      setTemplateSite(null);
    } else {
      loadTemplate(id);
    }
  }, [id, loadTemplate]);

  useEffect(() => {
    loadSites();
    const onNewToken = () => {
      loadSites();
      if (!loadedRef.current && id !== "new") loadTemplate(id);
    };
    document.addEventListener("newToken", onNewToken);
    return () => document.removeEventListener("newToken", onNewToken);
  }, [id, loadSites, loadTemplate]);

  // A new template starts on the site the user works in (or their only site).
  useEffect(() => {
    if (!isNew || !sites.length) return;
    const current = localStorage.getItem("locationId");
    const site = sites.find((s) => s.id === current)?.id ?? (sites.length === 1 ? sites[0].id : "");
    if (!site) return;
    setSaved((prev) => (prev.header.locationId ? prev : { ...prev, header: { ...prev.header, locationId: site } }));
    setHeader((prev) => (prev.locationId ? prev : { ...prev, locationId: site }));
  }, [isNew, sites]);

  /* ---------------- Derived state ---------------- */

  const current = useMemo(() => ({ header, lines }), [header, lines]);
  const changes = useMemo(() => listChanges(saved, current), [saved, current]);
  const invalidCount = useMemo(() => invalidLineCount(lines), [lines]);
  const headerProblems = headerErrors(header);
  const firstHeaderProblem = Object.values(headerProblems)[0];
  const dirty = changes.length > 0;
  const visibleLines = useMemo(() => filterLines(lines, query), [lines, query]);
  const inTemplate = useMemo(() => unitsOnTemplate(lines), [lines]);
  const duplicates = useMemo(() => duplicateLines(lines), [lines]);
  const siteOptions = useMemo(
    () => (templateSite && !sites.some((s) => s.id === templateSite.id) ? [templateSite, ...sites] : sites),
    [sites, templateSite],
  );

  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;

  /* ---------------- Leave guard ---------------- */

  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  // The host asks before a company switch; Discard and leave calls proceed() to finish it.
  useEffect(() => {
    const onHostLeave = (event: Event) => {
      if (!dirtyRef.current) return;
      event.preventDefault();
      const proceed = (event as CustomEvent).detail?.proceed;
      setAddOpen(false);
      setLeave({ kind: "host", proceed: typeof proceed === "function" ? () => proceed() : null });
    };
    document.addEventListener("finex:beforeLeave", onHostLeave);
    return () => document.removeEventListener("finex:beforeLeave", onHostLeave);
  }, []);

  const discard = () => reset(saved);

  const goBack = () => {
    if (dirtyRef.current) setLeave({ kind: "back" });
    else webNavigate(listPath);
  };

  const confirmLeave = () => {
    const request = leave;
    dirtyRef.current = false;
    discard();
    setLeave(null);
    if (request?.kind === "back") webNavigate(listPath);
    else request?.proceed?.();
  };

  /* ---------------- Editing ---------------- */

  const setField = <K extends keyof TemplateHeader>(field: K, value: TemplateHeader[K]) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
    setServerErrors((prev) => (prev[field] ? { ...prev, [field]: "" } : prev));
  };

  const updateLine = (key: string, patch: Partial<TemplateLine>) =>
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...patch } : line)));

  const removeLine = (key: string, index: number) => {
    setLines((prev) => prev.filter((line) => line.key !== key));
    // Focus stays in the table: the next row's remove button, else Add products.
    setTimeout(() => {
      const buttons = tableRef.current?.querySelectorAll<HTMLButtonElement>("button[data-ot-remove]");
      const next = buttons && buttons.length ? buttons[Math.min(index, buttons.length - 1)] : null;
      (next ?? addButtonRef.current)?.focus();
    }, 0);
  };

  // Safari never focuses a clicked button, so focus goes back to Add products by hand.
  const closeAdd = () => {
    setAddOpen(false);
    setTimeout(() => addButtonRef.current?.focus({ preventScroll: true }), 0);
  };

  const handleAdd = (picks: ProductPick[]) => {
    setLines((prev) => addProducts(prev, picks));
    setQuery("");
    closeAdd();
  };

  // Enter moves to the same column on the next row (Shift+Enter: previous), never saving.
  const onQtyKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, field: QtyField, index: number) => {
    if (e.key !== "Enter" || e.ctrlKey || e.metaKey || e.altKey || e.nativeEvent.isComposing) return;
    e.preventDefault();
    const target = tableRef.current?.querySelector<HTMLInputElement>(
      `input[data-ot-field="${field}"][data-ot-row="${index + (e.shiftKey ? -1 : 1)}"]`,
    );
    if (target) {
      target.focus();
      target.select();
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "f" && filterRef.current && !addOpen && !leave) {
        e.preventDefault();
        filterRef.current.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [addOpen, leave]);

  /* ---------------- Saving ---------------- */

  // Marks every problem and takes the user to the first one, scrolled clear of the save bar.
  const showProblems = (problem: Problem) => {
    setAttempted(true);
    setTouched((prev) => ({ ...prev, templateName: true, locationId: true }));
    let id: string;
    if (problem.kind === "header") {
      if (problem.field === "validTo") setDetailsOpen(true);
      id = HEADER_FIELD_ID[problem.field];
    } else {
      if (!visibleLines.some((line) => line.key === problem.key)) setQuery("");
      id = problem.field === "productUOMId" ? `ot-unit-${problem.key}` : `ot-${problem.field}-${problem.key}`;
    }
    const fallback = problem.kind === "line" ? `ot-lowQty-${problem.key}` : id;
    setTimeout(() => {
      const field = document.getElementById(id) ?? document.getElementById(fallback);
      if (!field) return;
      field.scrollIntoView({ block: "center" });
      field.focus({ preventScroll: true });
    }, 0);
  };

  const save = async () => {
    if (!dirty) return;
    const problem = firstProblem(header, lines);
    if (problem) {
      showProblems(problem);
      return;
    }
    const limitErrors = validateAgainstLimits({ ...header }, limits, language, ["templateName", "description", "locationId"]);
    if (Object.keys(limitErrors).length) {
      setServerErrors(limitErrors);
      if (limitErrors.description) setDetailsOpen(true);
      return;
    }
    const payload = buildPayload(header, lines);
    webLoadingTrue();
    try {
      if (isNew) {
        const response: any = await createOrderTemplates(payload);
        const newId = response?.data?.data?.id;
        dirtyRef.current = false;
        setSaved(current);
        showToast({ message: t("Order Template Created !"), type: "success", duration: 20000 });
        if (newId) {
          setCreatedId(String(newId));
          webNavigate(listPath + "/" + newId);
        } else {
          webNavigate(listPath);
        }
      } else {
        await updateOrderTemplates(id, payload);
        showToast({ message: t("Order Template Updated !"), type: "success", duration: 20000 });
        await loadTemplate(id);
      }
    } catch (error) {
      showApiError(error, {
        language,
        setFieldErrors: (fieldErrors) => {
          setServerErrors((prev) => ({ ...prev, ...fieldErrors }));
          if (fieldErrors.description || fieldErrors.validFrom || fieldErrors.validTo) setDetailsOpen(true);
        },
      });
    } finally {
      webLoadingFalse();
    }
  };

  /* ---------------- Rendering ---------------- */

  const lineName = (line: TemplateLine) => line.productName || (line.missing ? t("Product unavailable") : line.sku);
  // A product on more than one line is told apart by its unit.
  const lineLabel = (line: TemplateLine) => {
    const unit = (inTemplate.get(line.productId)?.size ?? 0) > 1 ? line.units.find((u) => u.id === line.productUOMId)?.name : "";
    return unit ? `${lineName(line)} (${unit})` : lineName(line);
  };

  const describe = (change: Change) => {
    if (change.kind === "field") return t(FIELD_LABEL[change.field]);
    const key = change.kind === "added" ? "Added {name}" : change.kind === "removed" ? "Removed {name}" : "Edited {name}";
    return fill(t(key), { name: change.name || t("Product unavailable") });
  };
  const items = changes.map(describe);
  const summary = items.length <= 2 ? items.join(", ") : `${items.slice(0, 2).join(", ")} ${fill(t("+{n} more"), { n: items.length - 2 })}`;
  const attention =
    attempted && invalidCount > 0
      ? invalidCount === 1
        ? t("1 line needs attention")
        : fill(t("{n} lines need attention"), { n: invalidCount })
      : dirty && firstHeaderProblem
        ? t(firstHeaderProblem)
        : "";

  const headerError = (field: "templateName" | "locationId" | "validTo" | "description" | "validFrom") =>
    serverErrors[field] ||
    ((touched[field] || field === "validTo") && (headerProblems as Record<string, string>)[field] ? t((headerProblems as Record<string, string>)[field]) : "");

  const title = header.templateName.trim() || saved.header.templateName.trim() || t(isNew ? "Add Order Template" : "Edit Order Template");

  const numberStyle = (invalid: boolean): React.CSSProperties => ({
    ...fieldStyle(phone, invalid),
    height: phone ? "44px" : "34px",
    borderRadius: "8px",
    padding: "0 10px",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  });

  const unitSelect = (line: TemplateLine, labelledBy: string, errors: LineErrors) => {
    if (!line.units.length) return <span style={{ color: TEXT_FAINT, fontSize: "13px" }}>—</span>;
    const errorId = errors.productUOMId ? `ot-err-${line.key}-productUOMId` : undefined;
    return (
      <select
        id={`ot-unit-${line.key}`}
        className="ot-field"
        value={line.productUOMId}
        onChange={(e) => updateLine(line.key, { productUOMId: e.target.value })}
        aria-labelledby={labelledBy}
        aria-invalid={errors.productUOMId ? true : undefined}
        aria-describedby={errorId}
        style={{ ...fieldStyle(phone, !!errors.productUOMId), height: phone ? "44px" : "34px", borderRadius: "8px", padding: "0 6px" }}
      >
        {!line.productUOMId && <option value="">{t("Select")}</option>}
        {line.units.map((unit) => (
          <option key={unit.id} value={unit.id}>
            {unit.name}
          </option>
        ))}
      </select>
    );
  };

  const qtyInput = (line: TemplateLine, index: number, field: QtyField, errors: LineErrors, nameId: string) => {
    const error = errors[field];
    // After a refused Save an empty Order qty is red; its reason is for screen readers only.
    const missing = field === "medQty" && attempted && missingOrderQty(line);
    return (
      <>
        <input
          type="text"
          inputMode="decimal"
          autoComplete="off"
          enterKeyHint="next"
          maxLength={12}
          className="ot-field"
          id={`ot-${field}-${line.key}`}
          value={line[field]}
          placeholder={field === "highQty" ? t("No limit") : undefined}
          onChange={(e) => updateLine(line.key, { [field]: e.target.value } as Partial<TemplateLine>)}
          onKeyDown={(e) => onQtyKeyDown(e, field, index)}
          data-ot-field={field}
          data-ot-row={index}
          aria-labelledby={phone ? undefined : `${COLUMN_ID[field]} ${nameId}${line.units.length ? ` ot-unit-${line.key}` : ""}`}
          aria-invalid={error || missing ? true : undefined}
          aria-describedby={error ? `ot-err-${line.key}-${field}` : missing ? `ot-missing-${line.key}` : undefined}
          style={numberStyle(!!error || missing)}
        />
        {missing && (
          <span id={`ot-missing-${line.key}`} style={VISUALLY_HIDDEN}>
            {t(MSG.orderQtyRequired)}
          </span>
        )}
      </>
    );
  };

  const removeButton = (line: TemplateLine, index: number) => (
    <button
      type="button"
      className="ot-icon-btn"
      data-ot-remove=""
      onClick={() => removeLine(line.key, index)}
      aria-label={fill(t("Remove {name}"), { name: lineLabel(line) })}
      title={t("Remove")}
      style={{
        width: phone ? "44px" : "32px",
        height: phone ? "44px" : "32px",
        borderRadius: "999px",
        border: "none",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        flex: "none",
      }}
    >
      <Icon name="close" color="currentColor" size={18} />
    </button>
  );

  const errorMessages = (line: TemplateLine, errors: LineErrors, withLabels: boolean) =>
    (["productUOMId", ...QTY_FIELDS] as const)
      .filter((field) => errors[field])
      .map((field) => (
        <p key={field} id={`ot-err-${line.key}-${field}`} style={errorTextStyle}>
          {withLabels && field !== "productUOMId" ? `${t(COLUMN_LABEL[field])}: ` : ""}
          {t(errors[field] as string)}
        </p>
      ));

  const nameBlock = (line: TemplateLine, nameId: string) => (
    <div style={{ minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
        <span
          id={nameId}
          title={lineName(line)}
          style={{ ...ellipsis, fontSize: phone ? "15px" : "14px", fontWeight: 500, lineHeight: "18px", color: line.missing ? TEXT_DIM : TEXT }}
        >
          {lineName(line)}
        </span>
      </div>
      {line.sku && (
        <div style={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0, marginTop: phone ? "2px" : 0 }}>
          <span title={line.sku} style={{ ...ellipsis, fontFamily: MONO, fontSize: "11.5px", lineHeight: "15px", color: TEXT_DIM }}>
            {line.sku}
          </span>
        </div>
      )}
    </div>
  );

  const th: React.CSSProperties = {
    height: "40px",
    padding: "0 8px",
    backgroundColor: HEAD_BG,
    borderBottom: `1px solid ${BORDER_SOFT}`,
    fontSize: "12.5px",
    fontWeight: 600,
    color: TEXT_DIM,
    textAlign: "left",
    whiteSpace: "nowrap",
  };

  const desktopTable = (
    <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, tableLayout: "fixed" }}>
      <colgroup>
        <col />
        <col style={{ width: "170px" }} />
        <col style={{ width: "132px" }} />
        <col style={{ width: "132px" }} />
        <col style={{ width: "148px" }} />
        <col style={{ width: "56px" }} />
      </colgroup>
      <thead>
        <tr>
          <th scope="col" style={{ ...th, paddingLeft: "16px" }}>{t("Product")}</th>
          <th scope="col" id="ot-col-unit" style={th}>{t("Order unit")}</th>
          {QTY_FIELDS.map((field) => (
            <th key={field} scope="col" id={COLUMN_ID[field]} style={{ ...th, textAlign: "right", paddingRight: "12px" }}>
              {t(COLUMN_LABEL[field])}
            </th>
          ))}
          <th scope="col" style={th}>
            <span style={VISUALLY_HIDDEN}>{t("Remove")}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {visibleLines.map((line, index) => {
          const errors = visibleLineErrors(line, duplicates.has(line.key));
          const hasErrors = Object.keys(errors).length > 0;
          const nameId = `ot-name-${line.key}`;
          const td: React.CSSProperties = {
            height: "46px",
            padding: "0 8px",
            borderBottom: hasErrors ? "none" : `1px solid ${BORDER_SOFT}`,
            verticalAlign: "middle",
          };
          return (
            <React.Fragment key={line.key}>
              <tr className="ot-row">
                <td style={{ ...td, paddingLeft: "16px" }}>{nameBlock(line, nameId)}</td>
                <td style={td}>{unitSelect(line, `ot-col-unit ${nameId}`, errors)}</td>
                {QTY_FIELDS.map((field) => (
                  <td key={field} style={td}>
                    {qtyInput(line, index, field, errors, nameId)}
                  </td>
                ))}
                <td style={{ ...td, textAlign: "center" }}>{removeButton(line, index)}</td>
              </tr>
              {hasErrors && (
                <tr>
                  <td style={{ borderBottom: `1px solid ${BORDER_SOFT}`, backgroundColor: ERROR_BG }} />
                  <td colSpan={5} style={{ padding: "0 8px 10px", borderBottom: `1px solid ${BORDER_SOFT}`, backgroundColor: ERROR_BG }}>
                    {errorMessages(line, errors, true)}
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );

  const phoneList = (
    <div>
      <span id="ot-unit-label" style={VISUALLY_HIDDEN}>
        {t("Order unit")}
      </span>
      {visibleLines.map((line, index) => {
        const errors = visibleLineErrors(line, duplicates.has(line.key));
        const nameId = `ot-name-${line.key}`;
        return (
          <div
            key={line.key}
            role="group"
            aria-labelledby={line.units.length ? `${nameId} ot-unit-${line.key}` : nameId}
            style={{ padding: "12px 0", borderBottom: `1px solid ${BORDER_SOFT}` }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div style={{ flex: 1, minWidth: 0 }}>{nameBlock(line, nameId)}</div>
              <div style={{ width: "116px", flex: "none" }}>{unitSelect(line, `ot-unit-label ${nameId}`, errors)}</div>
              {removeButton(line, index)}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "8px", marginTop: "10px" }}>
              {QTY_FIELDS.map((field) => (
                <div key={field} style={{ minWidth: 0 }}>
                  <label htmlFor={`ot-${field}-${line.key}`} style={{ ...labelStyle, fontSize: "12px", marginBottom: "4px", ...ellipsis }}>
                    {t(PHONE_LABEL[field])}
                  </label>
                  {qtyInput(line, index, field, errors, nameId)}
                </div>
              ))}
            </div>
            {errorMessages(line, errors, true)}
          </div>
        );
      })}
    </div>
  );

  const emptyBox = (heading: string, body?: string) => (
    <div
      style={{
        padding: phone ? "28px 16px" : "36px 24px",
        border: `1px dashed ${BORDER}`,
        borderRadius: "16px",
        textAlign: "center",
        backgroundColor: "#FFFFFF",
      }}
    >
      <p style={{ margin: 0, fontSize: "15px", fontWeight: 600, color: INK }}>{heading}</p>
      {body && <p style={{ margin: "4px 0 0", fontSize: "13.5px", color: TEXT_DIM }}>{body}</p>}
    </div>
  );

  const statusLine = (
    <p style={{ margin: 0, fontSize: "14px", lineHeight: "20px", color: TEXT_MUTED, ...(phone ? {} : ellipsis) }}>
      {dirty ? (
        <>
          <strong style={{ fontWeight: 600, color: TEXT }}>
            {changes.length === 1 ? t("1 unsaved change") : fill(t("{n} unsaved changes"), { n: changes.length })}
          </strong>
          {summary && !attention ? <span style={{ color: TEXT_DIM }}>{` · ${summary}`}</span> : null}
        </>
      ) : (
        <span style={{ color: TEXT_DIM }}>{t("No unsaved changes")}</span>
      )}
      {attention ? <span style={{ color: "#8A5A00", fontWeight: 500 }}>{` · ${attention}`}</span> : null}
    </p>
  );

  const saveButton = (
    <PillButton
      variant="primary"
      large={phone}
      disabled={!dirty}
      {...kb.primaryProps(save)}
      style={phone ? { flex: 1 } : undefined}
    >
      {t("Save template")}
    </PillButton>
  );

  const saveBar = phone ? (
    <div
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: TABBAR_OFFSET,
        zIndex: 35,
        padding: "10px 16px",
        backgroundColor: "#FFFFFF",
        borderTop: `1px solid ${BORDER_SOFT}`,
        boxShadow: "0 -6px 18px rgba(5,41,42,.06)",
        fontFamily: FONT,
      }}
    >
      <div aria-live="polite" style={{ marginBottom: "8px" }}>
        {statusLine}
      </div>
      <div style={{ display: "flex", gap: "10px" }}>
        <PillButton large disabled={!dirty} onClick={discard} style={{ flex: 1 }}>
          {t("Discard")}
        </PillButton>
        {saveButton}
      </div>
    </div>
  ) : (
    <div
      style={{
        position: "sticky",
        bottom: 0,
        zIndex: 5,
        flex: "none",
        display: "flex",
        alignItems: "center",
        gap: "16px",
        padding: "12px 0",
        backgroundColor: "#FFFFFF",
        borderTop: `1px solid ${BORDER_SOFT}`,
      }}
    >
      <div aria-live="polite" style={{ flex: 1, minWidth: 0 }}>
        {statusLine}
      </div>
      <PillButton disabled={!dirty} onClick={discard}>
        {t("Discard")}
      </PillButton>
      {saveButton}
    </div>
  );

  const nameError = headerError("templateName");
  const siteError = headerError("locationId");
  const descriptionError = headerError("description");
  const validFromError = headerError("validFrom");
  const validToError = headerError("validTo");

  return (
    <div {...kb.scopeProps}>
      <div style={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column", fontFamily: FONT, color: TEXT }}>
        <style>{OT_CSS}</style>
        <div
          className="scroll-container"
          style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", overflowX: "hidden", paddingBottom: phone ? `${PHONE_BAR_SPACE}px` : "24px" }}
        >
          <button
            type="button"
            className="ot-link"
            onClick={goBack}
            style={{ display: "inline-flex", alignItems: "center", gap: "8px", minHeight: phone ? "44px" : "28px", padding: 0, border: "none", background: "none", cursor: "pointer" }}
          >
            <BackIcon />
            <span style={{ fontSize: "14px", fontWeight: 500, color: TEXT }}>{t("Back")}</span>
          </button>
          <h1 style={{ margin: phone ? "8px 0 16px" : "12px 0 20px", fontSize: phone ? "20px" : "22px", lineHeight: phone ? "28px" : "30px", fontWeight: 600, color: INK, overflowWrap: "anywhere" }}>
            {title}
          </h1>

          <div style={{ display: "grid", gridTemplateColumns: phone ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))", gap: phone ? "16px" : "20px", maxWidth: "880px" }}>
            <div>
              <label htmlFor="ot-name" style={labelStyle}>
                {t("Template name")} <span style={{ color: DESTRUCTIVE }}>*</span>
              </label>
              <input
                id="ot-name"
                type="text"
                className="ot-field"
                autoComplete="off"
                value={header.templateName}
                {...limitProps(limits, "templateName")}
                onChange={(e) => setField("templateName", e.target.value)}
                onBlur={() => setTouched((prev) => ({ ...prev, templateName: true }))}
                aria-required="true"
                aria-invalid={nameError ? true : undefined}
                aria-describedby={nameError ? "ot-name-error" : undefined}
                style={fieldStyle(phone, !!nameError)}
              />
              <FieldCounter value={header.templateName} max={limits.templateName?.maxLength} />
              {nameError && (
                <p id="ot-name-error" style={errorTextStyle}>
                  {nameError}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="ot-site" style={labelStyle}>
                {t("Site")} <span style={{ color: DESTRUCTIVE }}>*</span>
              </label>
              <select
                id="ot-site"
                className="ot-field"
                value={header.locationId}
                onChange={(e) => setField("locationId", e.target.value)}
                onBlur={() => setTouched((prev) => ({ ...prev, locationId: true }))}
                aria-required="true"
                aria-invalid={siteError ? true : undefined}
                aria-describedby={siteError ? "ot-site-error" : undefined}
                style={{ ...fieldStyle(phone, !!siteError), padding: "0 8px" }}
              >
                <option value="" disabled>
                  {t("Choose a site")}
                </option>
                {siteOptions.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </select>
              {siteError && (
                <p id="ot-site-error" style={errorTextStyle}>
                  {siteError}
                </p>
              )}
            </div>
          </div>

          <div style={{ marginTop: "14px", maxWidth: "880px" }}>
            <button
              type="button"
              className="ot-disclosure"
              aria-expanded={detailsOpen}
              aria-controls="ot-details"
              onClick={() => setDetailsOpen((open) => !open)}
              style={{ display: "inline-flex", alignItems: "center", gap: "6px", minHeight: phone ? "44px" : "32px", padding: 0, border: "none", background: "none", cursor: "pointer" }}
            >
              <span style={{ display: "inline-flex", transform: detailsOpen ? "rotate(90deg)" : "none", transition: "transform 150ms ease" }}>
                <Icon name="chev" size={16} color={TEXT_MUTED} />
              </span>
              <span style={{ fontSize: "14px", fontWeight: 500, color: TEXT_MUTED }}>{t("Description and valid dates")}</span>
            </button>
            <div
              id="ot-details"
              hidden={!detailsOpen}
              style={{ display: detailsOpen ? "grid" : "none", gridTemplateColumns: phone ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))", gap: phone ? "16px" : "20px", marginTop: "8px" }}
            >
              <div style={{ gridColumn: "1 / -1" }}>
                <label htmlFor="ot-description" style={labelStyle}>
                  {t("Description")}
                </label>
                <textarea
                  id="ot-description"
                  className="ot-field"
                  value={header.description}
                  {...limitProps(limits, "description")}
                  onChange={(e) => setField("description", e.target.value)}
                  aria-invalid={descriptionError ? true : undefined}
                  aria-describedby={descriptionError ? "ot-description-error" : undefined}
                  rows={3}
                  style={{ ...fieldStyle(phone, !!descriptionError), height: "auto", minHeight: "84px", padding: "10px 12px", resize: "vertical", lineHeight: "20px" }}
                />
                <FieldCounter value={header.description} max={limits.description?.maxLength} />
                {descriptionError && (
                  <p id="ot-description-error" style={errorTextStyle}>
                    {descriptionError}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="ot-valid-from" style={labelStyle}>
                  {t("Valid from")}
                </label>
                <input
                  id="ot-valid-from"
                  type="date"
                  className="ot-field"
                  value={dayOf(header.validFrom)}
                  onChange={(e) => setField("validFrom", pickDay(e.target.value, saved.header.validFrom))}
                  aria-invalid={validFromError ? true : undefined}
                  aria-describedby={validFromError ? "ot-valid-from-error" : undefined}
                  style={fieldStyle(phone, !!validFromError)}
                />
                {validFromError && (
                  <p id="ot-valid-from-error" style={errorTextStyle}>
                    {validFromError}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="ot-valid-to" style={labelStyle}>
                  {t("Valid to")}
                </label>
                <input
                  id="ot-valid-to"
                  type="date"
                  className="ot-field"
                  value={dayOf(header.validTo)}
                  min={dayOf(header.validFrom) || undefined}
                  onChange={(e) => setField("validTo", pickDay(e.target.value, saved.header.validTo))}
                  aria-invalid={validToError ? true : undefined}
                  aria-describedby={validToError ? "ot-valid-to-error" : undefined}
                  style={fieldStyle(phone, !!validToError)}
                />
                {validToError && (
                  <p id="ot-valid-to-error" style={errorTextStyle}>
                    {validToError}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: phone ? "column" : "row",
              alignItems: phone ? "stretch" : "center",
              gap: phone ? "10px" : "12px",
              margin: phone ? "24px 0 4px" : "28px 0 12px",
            }}
          >
            <h2 style={{ flex: phone ? undefined : 1, margin: 0, fontSize: "16px", lineHeight: "24px", fontWeight: 600, color: INK }}>
              {t("Products")} <span style={{ fontWeight: 500, color: TEXT_DIM, fontVariantNumeric: "tabular-nums" }}>{lines.length}</span>
            </h2>
            {lines.length > 0 && (
              <div style={{ position: "relative", width: phone ? "100%" : "300px" }}>
                <span aria-hidden="true" style={{ position: "absolute", left: "11px", top: "50%", transform: "translateY(-50%)", display: "flex" }}>
                  <Icon name="search" size={18} />
                </span>
                <input
                  ref={filterRef}
                  type="search"
                  className="ot-field"
                  autoComplete="off"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("Find a product in this template")}
                  aria-label={t("Find a product in this template")}
                  style={{ ...fieldStyle(phone), height: phone ? "44px" : "36px", paddingLeft: "36px" }}
                />
              </div>
            )}
            <PillButton ref={addButtonRef} variant="primary" large={phone} onClick={() => setAddOpen(true)}>
              <Icon name="plus" size={16} color="#FFFFFF" />
              {t("Add products")}
            </PillButton>
          </div>

          <div ref={tableRef}>
            {lines.length === 0 ? (
              emptyBox(t("No products in this template yet"), t("Use Add products to choose what this template orders."))
            ) : visibleLines.length === 0 ? (
              emptyBox(t("No products in this template match your search"))
            ) : phone ? (
              phoneList
            ) : (
              <div style={{ border: `1px solid ${BORDER_SOFT}`, borderRadius: "16px", overflow: "hidden", backgroundColor: "#FFFFFF" }}>{desktopTable}</div>
            )}
          </div>
          {lines.length > 0 && (
            <p style={{ margin: "10px 0 0", fontSize: "12.5px", lineHeight: "18px", color: TEXT_DIM }}>
              {t("Reorder at: suggested when stock is at or below this · Order qty: how many go on the order · Max: most per order, empty = no limit")}
            </p>
          )}
        </div>
        {saveBar}
      </div>
      {addOpen && (
        <AddProductsDialog
          phone={phone}
          language={language}
          templateName={header.templateName}
          inTemplate={inTemplate}
          onClose={closeAdd}
          onAdd={handleAdd}
        />
      )}
      <LeaveDialog open={leave !== null} phone={phone} language={language} onKeep={() => setLeave(null)} onLeave={confirmLeave} />
    </div>
  );
};

export default OrderTemplatesDetail;
